import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, Typography, Button, CircularProgress, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarsIcon from "@mui/icons-material/Stars";
import BusinessIcon from "@mui/icons-material/Business";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { EventsAPI } from "api_inbusiness";
import { toast } from "react-toastify";
import ConfirmModal from "lib/components/Modals/ConfirmModal";
import styles from "./CheckAttendanceCustomEvent.module.scss";

const CheckAttendanceCustomEvent = ({
  open,
  setOpen = () => {},
  eventData,
  qrData,
  eventId,
}) => {
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);

  const getTicket = () => {
    if (!qrData) return;
    setLoading(true);
    setErrorMessage("");
    EventsAPI.getTicket(qrData)
      .then((res) => {
        if (res.ok && res.data) {
          setTicketData(res.data);
        } else {
          setErrorMessage("Biletul nu a putut fi găsit.");
        }
      })
      .catch((err) => {
        console.error("Error fetching ticket:", err);
        setErrorMessage("Cod QR invalid sau biletul nu a fost găsit în sistem.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleConfirmCheckin = () => {
    if (!ticketData?.id) return;
    setSubmitting(true);
    EventsAPI.scanTicket(ticketData.id, eventId)
      .then((res) => {
        if (res.ok === true) {
          toast.success("Check-in confirmat cu succes!");
          setOpen(false);
        }
      })
      .catch((error) => {
        const msg = error?.response?.data?.message;
        if (msg === "Ticket already scanned") {
          toast.error("Biletul a fost deja scanat anterior!");
          setTicketData((prev) => ({ ...prev, scanned: true }));
        } else if (msg === "Ticket not valid") {
          toast.error("Biletul nu este valid!");
          setTicketData((prev) => ({ ...prev, valid: false }));
        } else {
          toast.error("A intervenit o eroare la confirmarea biletului!");
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleRequestClose = (event, reason) => {
    // If ticket is valid and not yet scanned, require confirmation to abandon
    if (ticketData?.valid === true && ticketData?.scanned === false) {
      setOpenCancelConfirm(true);
    } else {
      setOpen(false);
    }
  };

  const handleConfirmCancel = () => {
    setOpenCancelConfirm(false);
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      setTicketData(null);
      setOpenCancelConfirm(false);
      getTicket();
    }
  }, [open, qrData]);

  // Determine current display state
  const isAlreadyScanned = ticketData?.scanned === true;
  const isInvalid = ticketData && ticketData.valid === false;
  const isVip = Boolean(ticketData?.isVip);
  const isValidAndUnscanned = ticketData && ticketData.valid === true && !isAlreadyScanned;

  // Paper and header CSS classes
  let paperClass = styles.paperGreen;
  let headerClass = styles.headerGreen;

  if (isAlreadyScanned || isInvalid || errorMessage) {
    paperClass = styles.paperRed;
    headerClass = styles.headerRed;
  } else if (isValidAndUnscanned && isVip) {
    paperClass = styles.paperYellow;
    headerClass = styles.headerYellow;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleRequestClose}
        maxWidth="sm"
        fullWidth
        classes={{ paper: `${styles.dialogPaper} ${paperClass}` }}
      >
        {loading ? (
          <Box sx={{ padding: "60px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress size={50} sx={{ color: "#5b80ba" }} />
            <Typography variant="h6" sx={{ color: "#334155", fontWeight: 600 }}>
              Se verifică biletul...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <div>
            <div className={`${styles.headerContainer} ${styles.headerRed}`}>
              <div className={styles.iconWrapper}>
                <ErrorOutlineIcon sx={{ fontSize: 48, color: "#ffffff" }} />
              </div>
              <Typography className={styles.headerTitle}>
                EROARE SCANARE
              </Typography>
              <Typography className={styles.headerSubtitle}>
                {errorMessage}
              </Typography>
            </div>
            <DialogContent className={styles.contentWrapper}>
              <div className={styles.actionsContainer}>
                <Button
                  variant="contained"
                  className={styles.closeButtonRed}
                  onClick={() => setOpen(false)}
                >
                  Înapoi la scanare
                </Button>
              </div>
            </DialogContent>
          </div>
        ) : (
          <div>
            {/* Header section with large color feedback */}
            <div className={`${styles.headerContainer} ${headerClass}`}>
              <div className={styles.iconWrapper}>
                {isAlreadyScanned ? (
                  <CancelIcon sx={{ fontSize: 52, color: "#ffffff" }} />
                ) : isInvalid ? (
                  <ErrorOutlineIcon sx={{ fontSize: 52, color: "#ffffff" }} />
                ) : isVip ? (
                  <StarsIcon sx={{ fontSize: 54, color: "#78350f" }} />
                ) : (
                  <CheckCircleIcon sx={{ fontSize: 52, color: "#ffffff" }} />
                )}
              </div>

              <Typography className={styles.headerTitle}>
                {isAlreadyScanned
                  ? "BILETUL A MAI FOST SCANAT!"
                  : isInvalid
                  ? "BILET INVALIDAT!"
                  : isVip
                  ? "⭐ BILET VIP ⭐"
                  : "BILET VALID"}
              </Typography>

              <Typography className={styles.headerSubtitle}>
                {isAlreadyScanned
                  ? "Acest bilet a fost deja utilizat pentru acces anterior"
                  : isInvalid
                  ? (ticketData.reason ? `Motiv: ${ticketData.reason}` : "Acest bilet a fost marcat ca nevalid")
                  : isVip
                  ? "BILET VALID — PARTICIPANT CU ACCES VIP"
                  : "Biletul este valid și nu a mai fost scanat"}
              </Typography>
            </div>

            {/* Ticket details card */}
            <DialogContent className={styles.contentWrapper}>
              {isValidAndUnscanned && isVip && (
                <div className={styles.vipBadgeNotice}>
                  👑 <span>ATENȚIE VOLUNTAR: Înmânează ecusonul / brățara VIP participantului!</span>
                </div>
              )}

              <div className={styles.ticketCard}>
                {/* Attendee Name */}
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Participant</span>
                  <Typography className={styles.infoValueLarge}>
                    {ticketData.fullName || "Fără nume specificat"}
                  </Typography>
                </div>

                {/* Company Name */}
                {ticketData.purchase?.company && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Companie</span>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <BusinessIcon sx={{ color: "#64748b", fontSize: 20 }} />
                      <Typography className={styles.infoValue}>
                        {ticketData.purchase.company}
                      </Typography>
                    </Box>
                  </div>
                )}

                {/* Event Name */}
                {(ticketData.event?.title || eventData?.title) && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Eveniment</span>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EventIcon sx={{ color: "#64748b", fontSize: 18 }} />
                      <Typography sx={{ fontSize: 14, color: "#475569", fontWeight: 600 }}>
                        {ticketData.event?.title || eventData?.title}
                      </Typography>
                    </Box>
                  </div>
                )}

                {/* Ticket Meta Details */}
                <div className={styles.ticketMetaRow}>
                  <span className={`${styles.metaChip} ${styles.metaChipTicket}`}>
                    <ConfirmationNumberIcon sx={{ fontSize: 16 }} />
                    {ticketData.serial} - {ticketData.number}
                  </span>

                  {isVip && (
                    <span className={`${styles.metaChip} ${styles.metaChipVip}`}>
                      ⭐ VIP
                    </span>
                  )}

                  {isAlreadyScanned && (
                    <span style={{ background: "#fee2e2", color: "#991b1b", padding: "6px 12px", borderRadius: "8px", fontWeight: 700, fontSize: "13px" }}>
                      Scanat anterior
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className={styles.actionsContainer}>
                {isValidAndUnscanned ? (
                  <>
                    {/* Primary Confirmation Button */}
                    <Button
                      variant="contained"
                      className={`${styles.confirmButton} ${isVip ? styles.confirmYellow : styles.confirmGreen}`}
                      onClick={handleConfirmCheckin}
                      disabled={submitting}
                      startIcon={
                        submitting ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          <CheckCircleIcon sx={{ fontSize: 24 }} />
                        )
                      }
                    >
                      {submitting ? "Se confirmă..." : "Confirmă Check-in-ul"}
                    </Button>

                    {/* Secondary Cancel Button */}
                    <Button
                      variant="outlined"
                      className={styles.cancelButton}
                      onClick={() => setOpenCancelConfirm(true)}
                      disabled={submitting}
                    >
                      Renunț la confirmare
                    </Button>
                  </>
                ) : (
                  /* Red state button (close and return to scanner) */
                  <Button
                    variant="contained"
                    className={styles.closeButtonRed}
                    onClick={() => setOpen(false)}
                  >
                    Înapoi la scanare
                  </Button>
                )}
              </div>
            </DialogContent>
          </div>
        )}
      </Dialog>

      {/* Confirmation modal if volunteer clicks "Renunț la confirmare" */}
      <ConfirmModal
        open={openCancelConfirm}
        setOpen={setOpenCancelConfirm}
        text="Ești sigur că vrei să renunți la confirmarea acestui bilet?"
        buttonYes="DA, RENUNȚ"
        buttonNo="NU, CONTINUĂ"
        onClickButtonYes={handleConfirmCancel}
        onClickButtonNo={() => setOpenCancelConfirm(false)}
      />
    </>
  );
};

export default CheckAttendanceCustomEvent;
